// In order to use in shell-models-lib, this is a copy of expression.util.ts (DataIntegrationApp\Product\Source\app\shared\utils\expression.util.ts)
/* eslint-disable @typescript-eslint/no-explicit-any */
import { generateMomentJSFormatString } from './datetime-format.util';
import { generateUUID } from './uuid.util';
import { stringIsNullOrEmpty } from './string.util';
import { isLogicAppExpression } from './expression.util';
import moment from 'moment';

export interface IStringMap<T> {
    [key: string]: T;
}

const stringResources = {
    Expression_ExpressionParameterTypeMismatch_ErrorMessage: 'The expected type of the parameter at index {0} for {1} is {2}, found {3}',
    Expression_FunctionCallLeftSide: 'function call',
    Expression_IdentifierInvalid_ErrorMessage: 'The identifier starts at index {0} is invalid',
    Expression_IndexLeftSide: 'left part of [] operator',
    Expression_IndexOfArray: 'number for array index',
    Expression_IndexOfObject: 'string for object property access',
    Expression_IndexRightSide: 'right part of [] operator',
    Expression_NullProperty_ErrorMessage: 'Can\'t get property {0}',
    Expression_NumberInvalid_ErrorMessage: 'The number starts at index {0} is invalid',
    Expression_ParameterNumberMismatch_ErrorMessage: 'Expected {0} parameter(s) for {1}, found {2}',
    Expression_ParameterTypeMismatch_ErrorMessage: 'The parameter at index {0} for {1} can\'t be {2}',
    Expression_PartiallyTypeMismatch_ErrorMessage: 'The expected type of {0} is {1}, found {2}',
    Expression_UnexpectedChar_ErrorMessage: 'Expected {0}, found {1} at {2}',
    Expression_UnexpectedThing_ErrorMessage: 'Expected {0}, found {1}',
    Expression_UnexpectedThing_Separator: ' or ',
    Expression_WholeExpressionTypeMismatch_ErrorMessage: 'The evaluated type of the expression is {0}, expected {1}',
    Expression_CannotGetParameters_ErrorMessage: 'Cannot get parameters from the expression.'
};

// ----------------------------------------------------------------------------------
// Parse
// ----------------------------------------------------------------------------------

export type ExpressionNodeType = 'Concat' | 'HardCodedText' | 'HardCodedNumber' | 'Expression' | 'FunctionCall' | 'IdPart' | 'HardCodedArray' | 'Index';

export interface IExpressionNode {
    // Start index of the node, currently not useful
    startIndex: number;
    // End index of the node, useful if a node have subnodes. Use the EndIndex of last subnode to find out the next char to parse
    endIndex: number;
    nodeType: ExpressionNodeType;

    // Keyword will be used for PlainText, HardCodedText, HardCodedNumber, IdPart
    keyword?: string;

    // Swallow null property error. For example, if "a" doesn't have property "b", "a.b" will throw an error, "a?.b" will simply be null
    swallowNullPropertyError?: boolean;

    subNodes?: IExpressionNode[];
}

/**
 * Parses an expression and returns an array of Logic App parameters.
 * Example:
 * Given expression: "@concat(variables('test'),variables('test2'))"
 * Returns:["@variables('test')", "@variables('test2')"]
 * @param expression A string that represents an expression.
 * @returns An array of strings containing the parameterized values.
 */
export function getLogicAppParametersFromExpression(expression: string): string[] | null {
    if (stringIsNullOrEmpty(expression)) {
        return null;
    }
    const rootNode = parseExpression(expression);
    const result: Set<string> = new Set<string>();
    getLogicAppParametersFromNodes(expression, rootNode, result);
    return result.size > 0 ? Array.from(result) : null;
}

export function convertExpressionToConcatArray(expression: any): string[] {
    if (expression == null) {
        return ['null'];
    }
    if (typeof expression !== 'string') {
        if (typeof expression === 'number' || typeof expression === 'boolean') {
            return [JSON.stringify(expression)];
        }
        return [`json('${JSON.stringify(expression).replace(/'/g, `''`)}')`];
    }

    if (expression === '@') {
        return [`'@'`];
    }
    if (expression[0] === '@' && expression[1] !== '@' && expression[1] !== '{') {
        return [expression.substring(1)];
    }

    let concatArray: string[] = [];

    let isExpression = false;
    let isString = false;
    let buffer = '';

    let index = 0;
    while (index < expression.length) {
        if (!isExpression) {
            if (expression[index] === '@') {
                const zeroIndex = index === 0;
                let curr = '@';

                if (expression[index + 1] === '{') {
                    index += 1;
                    concatArray.push(`'${buffer.replace(/'/g, `''`)}'`);
                    buffer = '';
                    isExpression = true;
                } else {
                    while (expression[index + 1] === '@') {
                        curr += '@';
                        index += 1;
                    }
                    buffer += expression[index + 1] === '{' || zeroIndex
                        ? curr.substring(1)
                        : curr;
                }
            } else {
                buffer += expression[index];
            }
        } else {
            if (!isString) {
                if (expression[index] === '}') {
                    concatArray.push(buffer);
                    buffer = '';
                    isExpression = false;
                } else {
                    buffer += expression[index];
                    if (expression[index] === `'`) {
                        isString = true;
                    }
                }
            } else {
                buffer += expression[index];

                if (expression[index] === `'`) {
                    if (expression[index + 1] === `'`) {
                        index += 1;
                        buffer += expression[index];
                    } else {
                        isString = false;
                    }
                }
            }
        }

        index += 1;
    }
    concatArray.push(`'${buffer.replace(/'/g, `''`)}'`);

    concatArray = concatArray.filter((str) => str !== '' && str !== `''`);

    if (concatArray.length === 0) {
        concatArray = [`''`];
    }

    return concatArray;
}

export function concatStyleExpression(expression: any): string {
    const concatArray = convertExpressionToConcatArray(expression);

    return concatArray.length > 1
        ? `concat(${concatArray.join(', ')})`
        : concatArray.length === 1
            ? concatArray[0]
            : `''`;
}

export function parseExpression(expression: string): IExpressionNode {
    // Single '@' means '@'
    if (expression === '@') {
        // Text
        return {
            startIndex: 0,
            endIndex: 0,
            nodeType: 'HardCodedText',
            keyword: '@'
        };
    }
    if (expression.length === 0 || expression.indexOf('@@') === 0 || expression.indexOf('@{') === 0 || !(expression.indexOf('@') === 0)) {
        // Not pure expression
        const result: IExpressionNode = {
            startIndex: 0,
            endIndex: 0,
            nodeType: 'Concat',
            subNodes: []
        };
        let nextNode = parseRoot(expression, 0);
        while (nextNode !== null) {
            result.subNodes.push(nextNode);
            // Expression ends with '}', need to shift for 1 more charactor
            result.endIndex = nextNode.endIndex + (nextNode.nodeType === 'Expression' ? 1 : 0);
            nextNode = parseRoot(expression, result.endIndex + 1);
        }
        return result;
    } else {
        // Pure expression
        const nodes = parsePureExpression(expression, 1, false);
        validateExpectedChar(expression, nodes.endIndex + 1, [{ char: null }]);
        return nodes;
    }
}

export function isExpressionObject<T>(object: T): boolean {
    let propertyCount = 0;

    for (const _property in object) {
        ++propertyCount;
    }

    if (propertyCount === 2 && object.hasOwnProperty('type')
        && object.hasOwnProperty('value')
        && (object as any)['type'] === 'Expression') {
        return true;
    }

    return false;
}

export function containsParameter(obj: any): boolean {
    if (obj == null || obj.properties == null || obj.properties.parameters == null) {
        return false;
    } else if (typeof obj.properties.parameters === 'object') {
        // Only return true when it really has any parameter
        for (const _property in obj.properties.parameters) {
            return true;
        }
    }
    return false;
}

export function containsExpressionObject(obj: any): boolean {
    if (obj == null) {
        return false;
    } if (isExpressionObject(obj)) {
        return true;
    } else if (typeof obj === 'object') {
        for (const property in obj) {
            const value = (obj)[property];
            if (containsExpressionObject(value)) {
                return true;
            }
        }
    }
    return false;
}

export function containsLogicAppExpression(obj: unknown): boolean {
    if (obj == null) {
        return false;
    } else if (typeof obj === 'string') {
        return isLogicAppExpression(obj);
    } else if (typeof obj === 'object') {
        return Object.values(obj).some((value) => containsLogicAppExpression(value));
    }
    return false;
}

/**
 * @param expression The whole expression, need to make sure it's not a single "@" or a pure expression (start with "@", second char is not "@" or "{") before calling this function
 * @param pos The position of the start character to check
 */
function parseRoot(expression: string, pos: number): IExpressionNode {
    if (pos >= expression.length) {
        return null;
    }

    let str = '';
    let nextPos = pos;
    // Start of the string, check leading "@"s
    if (pos === 0) {
        // Start with "@@", N "@" will be treated as N-1 "@"
        if (expression.length > 1 && expression[0] === '@' && expression[1] === '@') {
            while (pos + 1 < expression.length && expression[pos + 1] === '@') {
                ++pos;
                str += '@';
            }
            // Handled leading "@"s
            nextPos = pos + 1;
        }
    }

    // Just start an expression
    if (nextPos + 1 < expression.length && expression[nextPos] === '@' && expression[nextPos + 1] === '{') {
        return parsePureExpression(expression, nextPos + 2, true);
    }

    // Check leading chars
    while (nextPos < expression.length) {
        pos = nextPos;
        const thisChar = expression[pos];
        // Single @ means @, @@{ means @{, @{ means an expression is going to start
        if (thisChar !== '@') {
            // Simply append the hard coded non-@ char
            str += thisChar;
            nextPos = pos + 1;
            nextPos = pos + (thisChar !== '@' || (pos + 1 < expression.length) ? 1 : 2);
        } else {
            if (pos + 1 < expression.length) {
                // Has next
                const nextChar = expression[pos + 1];
                if (nextChar === '@' && pos + 2 < expression.length && expression[pos + 2] === '{') {
                    // @@{, append the escaped @{
                    str += '@{';
                    pos += 2;
                    nextPos = pos + 1;
                } else if (nextChar === '{') {
                    // Going to start an expression
                    nextPos = expression.length;
                    --pos;
                } else {
                    // Simply append the hard coded @
                    str += '@';
                    nextPos = pos + 1;
                }
            } else {
                // Already end
                str += thisChar;
                nextPos = pos + 1;
            }
        }
    }
    // Text
    return {
        startIndex: 0,
        endIndex: pos,
        nodeType: 'HardCodedText',
        keyword: str
    };
}

function isLogicAppParameterPrefix(prefix: string): boolean {
    if (prefix === 'linkedService' || prefix === 'dataset' || prefix === 'pipeline' || prefix === 'trigger'
        || prefix === 'variables' || prefix === 'item' || prefix === 'items' || prefix === 'activity') {
        return true;
    }
    return false;
}

function getLogicAppParametersFromNodes(expression: string, rootNode: IExpressionNode, result: Set<string>): void {
    if (rootNode == null || result == null) {
        throw new Error(stringResources.Expression_CannotGetParameters_ErrorMessage);
    }
    if (rootNode.nodeType === 'Expression') {
        if (rootNode.subNodes != null && rootNode.subNodes.length > 0 && rootNode.subNodes[0].nodeType === 'IdPart' && isLogicAppParameterPrefix(rootNode.subNodes[0].keyword)) {
            const param: string = '@' + expression.substring(rootNode.startIndex, rootNode.endIndex + 1).trim();
            const splits: string[] = param.split('.');
            switch (rootNode.subNodes[0].keyword) {
                case 'dataset':
                case 'linkedservice':
                    result.add(splits[0] + '.' + splits[1]);
                    break;
                case 'pipeline':
                    if (splits[1] === 'parameters' || splits[1] === 'globalParameters') {
                        result.add(splits[0] + '.' + splits[1] + '.' + splits[2]);
                    } else {
                        result.add(splits[0] + '.' + splits[1]);
                    }
                    break;
                case 'variables':
                    result.add(splits[0]);
                    break;
                default:
                    result.add(param);
            }
            return;
        }
    }
    if (rootNode.subNodes != null && rootNode.subNodes.length > 0) {
        rootNode.subNodes.forEach((node) => getLogicAppParametersFromNodes(expression, node, result));
    }
}

function parsePureExpression(expression: string, pos: number, needEndChar: boolean): IExpressionNode {
    let result: IExpressionNode;

    // Ignore spaces
    let currentPos = pos;
    while (currentPos < expression.length && isSpace(expression[currentPos])) {
        ++currentPos;
    }

    // Check end of expression
    validateExpectedChar(expression, currentPos, [{}]);

    let currentChar = expression[currentPos];
    let node: IExpressionNode;
    if (currentChar === '\'') {
        // Hard coded string
        node = parseString(expression, currentPos + 1);
    } else if (isStartOfNumber(currentChar)) {
        // Hard coded number
        node = parseNumber(expression, currentPos);
        // eslint-disable-next-line
    } else if (currentChar === '[') {
        // Hard coded array
        node = parseArray(expression, currentPos + 1);
    } else {
        // Other
        node = parseSingleId(expression, currentPos);
    }
    result = {
        startIndex: currentPos,
        endIndex: node.endIndex,
        nodeType: 'Expression',
        subNodes: [node]
    };

    currentPos = result.endIndex + 1;
    while (currentPos < expression.length) {
        const startIndex = currentPos;
        currentChar = expression[currentPos];
        if (currentChar === '.') {
            // Get object from a dictionary
            ++currentPos;
            node = parseSingleId(expression, currentPos);
            result.subNodes.push(node);
            result.endIndex = node.endIndex;
            currentPos = result.endIndex + 1;
        } else if (currentChar === '(') {
            // Function call
            ++currentPos;
            const parameters = parseParameters(expression, currentPos, ')');

            // Check )
            currentPos = parameters.length > 0 ? parameters[parameters.length - 1].endIndex + 1 : currentPos;
            validateExpectedChar(expression, currentPos, [{ char: ')' }]);
            node = {
                startIndex,
                endIndex: currentPos,
                nodeType: 'FunctionCall',
                subNodes: parameters
            };
            result.subNodes.push(node);
            result.endIndex = currentPos;
            ++currentPos;
        } else if (currentChar === '[') {
            // Get object from an array or access property by string key
            ++currentPos;
            // Check node type
            node = parsePureExpression(expression, currentPos, false);
            if (node.nodeType === 'HardCodedArray') {
                // Impossible to hit Concat, FunctionCall, or Index here
                // Expression, HardCodedNumber, IdPart, and HardCodedText are valid in the Parser part
                // HardCodedText is valid for object property access (e.g., ['table'])
                // HardCodedArray must be invalid
                throw new Error(format(
                    stringResources.Expression_UnexpectedThing_ErrorMessage,
                    [stringResources.Expression_IndexOfArray, stringResources.Expression_IndexOfObject].join(stringResources.Expression_UnexpectedThing_Separator),
                    node.nodeType
                ));
            }
            // Check ]
            currentPos = node.endIndex + 1;
            validateExpectedChar(expression, currentPos, [{ char: ']' }]);
            node = {
                startIndex,
                endIndex: currentPos,
                nodeType: 'Index',
                subNodes: [node]
            };
            result.subNodes.push(node);
            result.endIndex = currentPos;
            ++currentPos;
        } else {
            // Exit the while statement
            currentPos = expression.length;
        }
    }

    // Ignore spaces
    currentPos = result.endIndex;
    while (currentPos + 1 < expression.length && isSpace(expression[currentPos + 1])) {
        ++currentPos;
    }

    // For expression, if the result contains only 1 sub node, use the sub node as result
    if (result.nodeType === 'Expression' && result.subNodes.length === 1) {
        const subNode = result.subNodes[0];
        subNode.startIndex = result.startIndex;
        subNode.endIndex = result.endIndex;
        result = subNode;
    }

    if (needEndChar) {
        validateExpectedChar(expression, currentPos + 1, [{ char: '}' }]);
    }
    // Adjust the start/end of the result
    result.startIndex = pos;
    result.endIndex = currentPos;
    return result;
}

function parseParameters(expression: string, pos: number, end: string): IExpressionNode[] {
    const result: IExpressionNode[] = [];
    if (pos < expression.length && expression[pos] === end) {
        return result;
    }
    let currentPos = pos;
    while (currentPos < expression.length) {
        const currentNode = parsePureExpression(expression, currentPos, false);
        if (currentNode === null) {
            // No more parameters
            currentPos = expression.length;
        } else {
            result.push(currentNode);
            currentPos = currentNode.endIndex + 1;
            validateExpectedChar(expression, currentPos, [
                {
                    char: ',', action: () => {
                        ++currentPos;
                    }
                },
                {
                    char: end, action: () => {
                        currentPos = expression.length;
                    }
                }]);
        }
    }
    return result;
}

function parseString(expression: string, pos: number): IExpressionNode {
    let stringResult = '';
    let currentPos = pos;
    let foundEnd = false;
    while (!foundEnd && currentPos < expression.length) {
        if (expression[currentPos] !== '\'') {
            stringResult += expression[currentPos];
            ++currentPos;
        } else {
            // Escape ' by using 2 's
            if (currentPos + 1 < expression.length && expression[currentPos + 1] === '\'') {
                stringResult += '\'';
                currentPos += 2;
            } else {
                foundEnd = true;
            }
        }
    }

    // Check end charactor
    validateExpectedChar(expression, currentPos, [{}]);
    return {
        startIndex: pos - 1, // "'"
        endIndex: currentPos,
        nodeType: 'HardCodedText',
        keyword: stringResult
    };
}

function parseArray(expression: string, pos: number): IExpressionNode {
    const startIndex = pos - 1; // "["
    const parameters = parseParameters(expression, pos, ']');
    pos = parameters.length > 0 ? parameters[parameters.length - 1].endIndex + 1 : pos;
    // Check "]"
    validateExpectedChar(expression, pos, [{ char: ']' }]);
    return {
        startIndex,
        endIndex: pos,
        nodeType: 'HardCodedArray',
        subNodes: parameters
    };
}

function parseNumber(expression: string, pos: number): IExpressionNode {
    const rx = /^([\-]?[0-9]+(\.[0-9]+)?)/;
    const match = rx.exec(expression.substring(pos));
    if (match) {
        return {
            startIndex: pos,
            endIndex: pos + match[1].length - 1,
            keyword: match[1],
            nodeType: 'HardCodedNumber'
        };
    }
    throw new Error(format(stringResources.Expression_NumberInvalid_ErrorMessage, '' + pos));
}

function parseSingleId(expression: string, pos: number): IExpressionNode {
    const rx = /^([_a-z][_a-z0-9]*)/i;
    const match = rx.exec(expression.substring(pos));
    if (match) {
        const result: IExpressionNode = {
            startIndex: pos,
            endIndex: pos + match[1].length - 1,
            keyword: match[1],
            nodeType: 'IdPart'
        };
        parseSwallowNullPropertyError(expression, result);
        return result;
    }
    throw new Error(format(stringResources.Expression_IdentifierInvalid_ErrorMessage, '' + pos));
}

function parseSwallowNullPropertyError(expression: string, node: IExpressionNode): void {
    if (node.endIndex + 1 < expression.length && expression[node.endIndex + 1] === '?') {
        ++node.endIndex;
        node.swallowNullPropertyError = true;
    }
}

function isSpace(ch: string): boolean {
    return ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r';
}

function isStartOfNumber(ch: string): boolean {
    return ch === '-' || ch === '0' || ch === '1' || ch === '2' || ch === '3' || ch === '4' || ch === '5' || ch === '6' || ch === '7' || ch === '8' || ch === '9';
}

interface IExpectedCharAndAction {
    // Single charactor string means a simple charactor
    // Null means end of the string
    // Undefined means not the end of the string
    char?: string;
    action?: () => void;
}

// Can be used to check the reserved charactors for start, for example, "[" or "(", but currently only using it to check the end charactors to make the code readable
function validateExpectedChar(expression: string, index: number, expectedCharsAndActions: IExpectedCharAndAction[]): void {
    const currentChar: string = index < expression.length ? expression[index] : null;
    const actions = expectedCharsAndActions.filter((expected) => expected.char === currentChar || (expected.char === undefined && currentChar !== null));
    if (actions.length > 0) {
        if (actions[0].action !== undefined) {
            actions[0].action();
        }
    } else {
        throw new Error(format(
            stringResources.Expression_UnexpectedChar_ErrorMessage,
            expectedCharsAndActions.map((expected) => getDisplayString(expected.char)).join(stringResources.Expression_UnexpectedThing_Separator),
            getDisplayString(currentChar),
            '' + index
        ));
    }
}

function getDisplayString(char: string): string {
    if (char === undefined) {
        return 'non-EOF';
    } else if (char === null) {
        return 'EOF';
    } else {
        return '\'' + char + '\'';
    }
}

function format(template: string, ...args: string[]): string {
    return (args || []).reduce((prev, curr, index) => prev.replace(new RegExp(`\\{${index}\\}`, 'g'), curr), template);
}

// ----------------------------------------------------------------------------------
// Evaluate
// ----------------------------------------------------------------------------------

export type TypeNameForExpression = 'Null' | 'String' | 'Number' | 'Boolean' | 'Array' | 'Function' | 'Object';

export function evaluateExpression(expression: string, parameters: any): any {
    // Parse
    const rootNode: IExpressionNode = parseExpression(expression);

    // Evaluate
    return evaluateNode(rootNode, parameters);
}

function evaluateNode(node: IExpressionNode, parameters: any): any {
    // "Concat" | "HardCodedText" | "HardCodedNumber" | "Expression" | "FunctionCall" | "IdPart" | "HardCodedArray" | "Index";
    switch (node.nodeType) {
        case 'Concat':
            return evaluateConcatNode(node, parameters);
        case 'HardCodedText':
            return node.keyword;
        case 'HardCodedNumber':
            return evaluateHardCodedNumber(node.keyword);
        case 'Expression':
            return evaluateExpressionNode(node, parameters);
        case 'IdPart':
            return evaluateIdPart(node.keyword, parameters, false);
        case 'HardCodedArray':
            return node.subNodes.map((subNode) => evaluateNode(subNode, parameters));
        // Index and FunctionCall must be part of an Expression, not a standalone node
        // case "Index":
        // case "FunctionCall":
        default:
            throw new Error('Something unexpected happened when evaluating the expression');
    }
}

function evaluateConcatNode(node: IExpressionNode, parameters: any): string {
    let result = '';
    node.subNodes.forEach((subNode, index) => {
        const subNodeResult = evaluateNode(subNode, parameters);
        const typeOfSubNodeResult = getTypeNameForExpression(subNodeResult);
        if (typeOfSubNodeResult === 'String') {
            result += subNodeResult;
        } else {
            throw new Error(format(stringResources.Expression_ExpressionParameterTypeMismatch_ErrorMessage, '' + index, 'concat', 'String', typeOfSubNodeResult));
        }
    });
    return result;
}

function evaluateHardCodedNumber(keyword: string): number {
    if (keyword.indexOf('.') >= 0) {
        return parseFloat(keyword);
    }
    return parseInt(keyword, 10);
}

function evaluateIdPart(name: string, parrentObj: any, swallowNullPropertyError: boolean): any {
    let result: any;
    if (parrentObj == null) {
        if (!swallowNullPropertyError) {
            throw new Error(format(stringResources.Expression_NullProperty_ErrorMessage, name));
        }
        result = null;
    } else {
        result = parrentObj[name];
        if (result === undefined) {
            if (!swallowNullPropertyError) {
                throw new Error(format(stringResources.Expression_NullProperty_ErrorMessage, name));
            }
            result = null;
        }
    }
    return result;
}

function evaluateExpressionNode(node: IExpressionNode, parameters: any): any {
    // (IdPart|HardCodedText|HardCodedNumber|HardCodedArray)((IdPart|Index|FunctionCall)*
    let swallowNullPropertyError = false;
    if (node.subNodes.length === 0) {
        return '';
    }
    let result: any = parameters;
    node.subNodes.forEach((subNode) => {
        switch (subNode.nodeType) {
            case 'IdPart':
                result = evaluateIdPart(subNode.keyword, result, swallowNullPropertyError);
                swallowNullPropertyError = subNode.swallowNullPropertyError === true;
                break;
            case 'HardCodedText':
                result = subNode.keyword;
                swallowNullPropertyError = false;
                break;
            case 'HardCodedNumber':
                result = evaluateHardCodedNumber(subNode.keyword);
                break;
            case 'HardCodedArray':
                result = subNode.subNodes.map((subSubNode) => evaluateNode(subSubNode, parameters));
                swallowNullPropertyError = false;
                break;
            case 'Index':
                result = evaluateIndexNode(subNode, result, parameters);
                swallowNullPropertyError = false;
                break;
            case 'FunctionCall':
                result = evaluateFunctionNode(subNode, result, parameters);
                swallowNullPropertyError = false;
                break;
            // Concat must be a first level node
            // case "Concat":
            // Expression can be under another expression directly
            // case "Expression":
            default:
                throw new Error('Something unexpected happened when evaluating the expression');
        }
    });

    return result;
}

function evaluateIndexNode(node: IExpressionNode, parrentObj: any, parameters: any): any {
    const parrenType = getTypeNameForExpression(parrentObj);
    if (parrenType === 'Array') {
        const indexValue = evaluateNode(node.subNodes[0], parameters);
        const indexType = getTypeNameForExpression(indexValue);
        if (indexType !== 'Number') {
            throw new Error(format(stringResources.Expression_PartiallyTypeMismatch_ErrorMessage, stringResources.Expression_IndexRightSide, 'Number', indexType));
        }
        return parrentObj[indexValue];
    } else if (parrenType === 'Object') {
        const indexValue = evaluateNode(node.subNodes[0], parameters);
        const indexType = getTypeNameForExpression(indexValue);
        if (indexType !== 'String') {
            throw new Error(format(stringResources.Expression_PartiallyTypeMismatch_ErrorMessage, stringResources.Expression_IndexRightSide, 'String', indexType));
        }
        const result = parrentObj[indexValue];
        if (result === undefined) {
            throw new Error(format(stringResources.Expression_NullProperty_ErrorMessage, indexValue));
        }
        return result;
    } else {
        throw new Error(format(stringResources.Expression_PartiallyTypeMismatch_ErrorMessage, stringResources.Expression_IndexLeftSide, 'Array or Object', parrenType));
    }
}

function evaluateFunctionNode(node: IExpressionNode, parrentObj: any, parameters: any): any {
    const parrenType = getTypeNameForExpression(parrentObj);
    if (parrenType !== 'Function') {
        throw new Error(format(stringResources.Expression_PartiallyTypeMismatch_ErrorMessage, stringResources.Expression_FunctionCallLeftSide, 'Function', parrenType));
    } else {
        const functionCallParameters = node.subNodes.map((subNode) => evaluateNode(subNode, parameters));
        return parrentObj(...functionCallParameters);
    }
}

// ----------------------------------------------------------------------------------
// Collection
// ----------------------------------------------------------------------------------

function builtInContains(...args: any[]): boolean {
    checkBuiltInFunctionParameter('contains', 2, [['String', 'Array']], args);
    return args[0].indexOf(args[1]) >= 0;
}

function builtInLength(...args: any[]): number {
    checkBuiltInFunctionParameter('length', 1, [['String', 'Array']], args);
    return args[0].length;
}

// ----------------------------------------------------------------------------------
// String
// ----------------------------------------------------------------------------------

function builtInConcat(...args: any[]): string {
    let result = '';
    args.forEach((arg, index) => {
        checkBuiltInFunctionParameterType('concat', index, ['String'], args[0]);
        result += arg;
    });
    return result;
}

function builtInSubString(...args: any[]): string {
    checkBuiltInFunctionParameter('string', 3, [['String'], ['Number'], ['Number']], args);
    return (args[0] as string).substr(args[1], args[2]);
}

function builtInReplace(...args: any[]): string {
    checkBuiltInFunctionParameter('string', 3, [['String'], ['String'], ['String']], args);
    // Don't expect regreplace, so not using string.replace
    return (args[0] as string).split(args[1]).join(args[2]);
}

function builtInGuid(...args: any[]): string {
    // Temprorily ignoring format, because this should be a function with low priority
    checkBuiltInFunctionParameter('guid', 0, [], args);
    return generateUUID();
    // checkBuiltInFunctionParameter("guid", args.length === 1 ? 1 : 0, [["String"]], args);
    // let result = generateUUID();
    // if (args.length === 1) {

    // }
    // return result;
}

function builtInToLower(...args: any[]): string {
    checkBuiltInFunctionParameter('toLower', 1, [['String']], args);
    return args[0].toLowerCase();
}

function builtInToUpper(...args: any[]): string {
    checkBuiltInFunctionParameter('toUpper', 1, [['String']], args);
    return args[0].toUpperCase();
}

function builtInIndexOf(...args: any[]): number {
    checkBuiltInFunctionParameter('indexof', 2, [['String'], ['String']], args);
    return (args[0] as string).toLowerCase().indexOf((args[1] as string).toLowerCase());
}

function builtInLastIndexOf(...args: any[]): number {
    checkBuiltInFunctionParameter('lastindexof', 2, [['String'], ['String']], args);
    return (args[0] as string).toLowerCase().lastIndexOf((args[1] as string).toLowerCase());
}

function builtInStartsWith(...args: any[]): boolean {
    checkBuiltInFunctionParameter('startswith', 2, [['String'], ['String']], args);
    return (args[0] as string).toLowerCase().indexOf((args[1] as string).toLowerCase()) === 0;
}

function builtInEndsWith(...args: any[]): boolean {
    checkBuiltInFunctionParameter('endswith', 2, [['String'], ['String']], args);
    return (args[0] as string).toLowerCase().lastIndexOf((args[1] as string).toLowerCase()) === (args[0] as string).length - (args[1] as string).length;
}

function builtInSplit(...args: any[]): string[] {
    checkBuiltInFunctionParameter('split', 2, [['String'], ['String']], args);
    return (args[0] as string).split(args[1]);
}

// ----------------------------------------------------------------------------------
// Logical
// ----------------------------------------------------------------------------------

function builtInEquals(...args: any[]): boolean {
    checkBuiltInFunctionParameter('equals', 2, [], args);
    return args[0] === args[1];
}

function builtInLess(...args: any[]): boolean {
    checkBuiltInFunctionParameter('less', 2, [['Number', 'String'], ['Number', 'String']], args);
    return args[0] < args[1];
}

function builtInLessOrEquals(...args: any[]): boolean {
    checkBuiltInFunctionParameter('lessOrEquals', 2, [['Number', 'String'], ['Number', 'String']], args);
    return args[0] <= args[1];
}

function builtInGreater(...args: any[]): boolean {
    checkBuiltInFunctionParameter('greater', 2, [['Number', 'String'], ['Number', 'String']], args);
    return args[0] > args[1];
}

function builtInGreaterOrEquals(...args: any[]): boolean {
    checkBuiltInFunctionParameter('greaterOrEquals', 2, [['Number', 'String'], ['Number', 'String']], args);
    return args[0] >= args[1];
}

function builtInAnd(...args: any[]): boolean {
    checkBuiltInFunctionParameter('and', 2, [['Boolean'], ['Boolean']], args);
    return args[0] && args[1];
}

function builtInOr(...args: any[]): boolean {
    checkBuiltInFunctionParameter('or', 2, [['Boolean'], ['Boolean']], args);
    return args[0] || args[1];
}

function builtInNot(...args: any[]): boolean {
    checkBuiltInFunctionParameter('not', 1, [['Boolean']], args);
    return !args[0];
}

function builtInIf(...args: any[]): any {
    checkBuiltInFunctionParameter('if', 3, [['Boolean']], args);
    return args[0] ? args[1] : args[2];
}

// ----------------------------------------------------------------------------------
// Conversion
// ----------------------------------------------------------------------------------

function builtInInt(...args: any[]): number {
    checkBuiltInFunctionParameter('int', 1, [['String']], args);
    return parseInt(args[0], 10);
}

function builtInString(...args: any[]): string {
    checkBuiltInFunctionParameter('string', 1, [], args);
    return args[0].toString();
}

function builtInFloat(...args: any[]): number {
    checkBuiltInFunctionParameter('float', 1, [['String']], args);
    return parseFloat(args[0]);
}

function builtInBool(...args: any[]): boolean {
    checkBuiltInFunctionParameter('bool', 1, [], args);
    // Not quite sure about the logic here
    return !!args[0];
}

function builtInArray(...args: any[]): any[] {
    checkBuiltInFunctionParameter('array', 1, [], args);
    return [args[0]];
}

// ----------------------------------------------------------------------------------
// Math
// ----------------------------------------------------------------------------------

function builtInAdd(...args: any[]): number {
    checkBuiltInFunctionParameter('add', 2, [['Number'], ['Number']], args);
    return (args[0] as number) + (args[1] as number);
}

function builtInSub(...args: any[]): number {
    checkBuiltInFunctionParameter('sub', 2, [['Number'], ['Number']], args);
    return (args[0] as number) - (args[1] as number);
}

function builtInMul(...args: any[]): number {
    checkBuiltInFunctionParameter('mul', 2, [['Number'], ['Number']], args);
    return (args[0] as number) * (args[1] as number);
}

function builtInDiv(...args: any[]): number {
    checkBuiltInFunctionParameter('div', 2, [['Number'], ['Number']], args);
    return (args[0] as number) / (args[1] as number);
}

function builtInMod(...args: any[]): number {
    checkBuiltInFunctionParameter('mod', 2, [['Number'], ['Number']], args);
    return (args[0] as number) % (args[1] as number);
}

function builtInMin(...args: any[]): number {
    checkBuiltInFunctionParameter('min', args.length > 0 ? args.length : 1, [['Number', 'Array']], args);
    if (getTypeNameForExpression(args[0]) === 'Array') {
        return builtInMin(...args[0]);
    }
    return Math.min(...args);
}

function builtInMax(...args: any[]): number {
    checkBuiltInFunctionParameter('max', args.length > 0 ? args.length : 1, [['Number', 'Array']], args);
    if (getTypeNameForExpression(args[0]) === 'Array') {
        return builtInMax(...args[0]);
    }
    return Math.max(...args);
}

function builtInRange(...args: any[]): number[] {
    checkBuiltInFunctionParameter('range', 2, [['Number'], ['Number']], args);
    return (Array.apply(null, Array(args[1] as number)) as any[]).map((_, i) => i + (args[0] as number));
}

function builtInRand(...args: any[]): number {
    checkBuiltInFunctionParameter('rand', 2, [['Number'], ['Number']], args);
    return getRandomInt(args[0] as number, args[1] as number);
}

// https://stackoverflow.com/questions/1527803/generating-random-whole-numbers-in-javascript-in-a-specific-range
// Returns a random integer between min (inclusive) and max (inclusive)
function getRandomInt(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

// ----------------------------------------------------------------------------------
// Date
// ----------------------------------------------------------------------------------

function builtInUtcNow(...args: any[]): string {
    checkBuiltInFunctionParameter('utcNow', 0, [], args);
    return momentToISO8601Format(moment().utc());
}

function builtInAddSeconds(...args: any[]): string {
    checkBuiltInFunctionParameter('addseconds', args.length === 3 ? 3 : 2, [['String'], ['Number'], ['String']], args);
    let date = stringToMomentWithCSFormat(args[0] as string, args[2] as string);
    date = date.add(args[1] as number, 'seconds');
    return momentToISO8601Format(date);
}

function builtInAddMinutes(...args: any[]): string {
    checkBuiltInFunctionParameter('addminutes', args.length === 3 ? 3 : 2, [['String'], ['Number'], ['String']], args);
    let date = stringToMomentWithCSFormat(args[0] as string, args[2] as string);
    date = date.add(args[1] as number, 'minutes');
    return momentToISO8601Format(date);
}

function builtInAddHours(...args: any[]): string {
    checkBuiltInFunctionParameter('addhours', args.length === 3 ? 3 : 2, [['String'], ['Number'], ['String']], args);
    let date = stringToMomentWithCSFormat(args[0] as string, args[2] as string);
    date = date.add(args[1] as number, 'hours');
    return momentToISO8601Format(date);
}

function builtInAddDays(...args: any[]): string {
    checkBuiltInFunctionParameter('adddays', args.length === 3 ? 3 : 2, [['String'], ['Number'], ['String']], args);
    let date = stringToMomentWithCSFormat(args[0] as string, args[2] as string);
    date = date.add(args[1] as number, 'days');
    return momentToISO8601Format(date);
}

function builtInFormatDateTime(...args: any[]): string {
    checkBuiltInFunctionParameter('formatDateTime', args.length === 2 ? 2 : 1, [['String'], ['String']], args);
    const date = stringToMomentWithCSFormat(args[0] as string, undefined);
    const csDateTimeFormat = args[1] === undefined ? 'o' : args[1] as string;
    const momentDateTimeFormat = generateMomentJSFormatString(csDateTimeFormat);
    return date.format(momentDateTimeFormat);
}

function builtInStartOfHour(...args: any[]): string {
    checkBuiltInFunctionParameter('startOfHour', args.length === 2 ? 2 : 1, [['String'], ['String']], args);
    const date = stringToMomentWithCSFormat(args[0] as string, args[1] as string);
    return momentToISO8601Format(date.startOf('hours'));
}

function builtInStartOfDay(...args: any[]): string {
    checkBuiltInFunctionParameter('startOfDay', args.length === 2 ? 2 : 1, [['String'], ['String']], args);
    const date = stringToMomentWithCSFormat(args[0] as string, args[1] as string);
    return momentToISO8601Format(date.startOf('days'));
}

function builtInStartOfMonth(...args: any[]): string {
    checkBuiltInFunctionParameter('startOfMonth', args.length === 2 ? 2 : 1, [['String'], ['String']], args);
    const date = stringToMomentWithCSFormat(args[0] as string, args[1] as string);
    return momentToISO8601Format(date.startOf('months'));
}

function builtInDayOfWeek(...args: any[]): number {
    checkBuiltInFunctionParameter('dayOfWeek', 1, [['String']], args);
    const date = stringToMomentWithCSFormat(args[0] as string, undefined);
    return date.isoWeekday() % 7;
}

function builtInDayOfMonth(...args: any[]): number {
    checkBuiltInFunctionParameter('dayOfMonth', 1, [['String']], args);
    const date = stringToMomentWithCSFormat(args[0] as string, undefined);
    return date.date();
}

function builtInDayOfYear(...args: any[]): number {
    checkBuiltInFunctionParameter('dayOfYear', 1, [['String']], args);
    const date = stringToMomentWithCSFormat(args[0] as string, undefined);
    return date.dayOfYear();
}

function builtInTicks(...args: any[]): number {
    checkBuiltInFunctionParameter('ticks', 1, [['String']], args);
    const date = stringToMomentWithCSFormat(args[0] as string, undefined);
    return momentToTicks(date);
}

function stringToMomentWithCSFormat(str: string, csFormat: string): moment.Moment {
    const momentDateTimeFormat = csFormat === undefined ? generateMomentJSFormatString('o') : generateMomentJSFormatString(csFormat);
    return moment.utc(str as string, momentDateTimeFormat);
}

function momentToTicks(datetime: moment.Moment): number {
    return datetime.toDate().getTime() / 1000;
}

function momentToISO8601Format(datetime: moment.Moment): string {
    return datetime.format(generateMomentJSFormatString('o'));
}

export const builtInFunctions: IStringMap<any> = {
    // Collection
    contains: builtInContains,
    length: builtInLength,
    // empty
    // intersection
    // union
    // first
    // last
    // take
    // skip
    // join

    // String
    concat: builtInConcat,
    substring: builtInSubString,
    replace: builtInReplace,
    guid: builtInGuid, // Format not supported yet
    toLower: builtInToLower,
    toUpper: builtInToUpper,
    indexof: builtInIndexOf,
    lastindexof: builtInLastIndexOf,
    startswith: builtInStartsWith,
    endswith: builtInEndsWith,
    split: builtInSplit,

    // Logical
    equals: builtInEquals,
    less: builtInLess,
    lessOrEquals: builtInLessOrEquals,
    greater: builtInGreater,
    greaterOrEquals: builtInGreaterOrEquals,
    and: builtInAnd,
    or: builtInOr,
    not: builtInNot,
    if: builtInIf,

    // Conversion
    int: builtInInt,
    // eslint-disable-next-line id-blacklist
    string: builtInString,
    // json
    float: builtInFloat,
    bool: builtInBool,
    // coalesce
    // base64
    // base64ToBinary
    // base64ToString
    // Binary
    // dataUriToBinary
    // dataUriToString
    // dataUri
    // decodeBase64
    // encodeUriComponent
    // decodeUriComponent
    // decodeDataUri
    // uriComponent
    // UriComponentToBinary
    // uriComponentToString
    // xml
    // xpath
    array: builtInArray,
    // createArray
    // triggerFormDataValue
    // triggerFormDataMultiValues
    // triggerMultipartBody
    // formDataValue
    // formDataMultiValues
    // multipartBody

    // Math
    add: builtInAdd,
    sub: builtInSub,
    mul: builtInMul,
    div: builtInDiv,
    mod: builtInMod,
    min: builtInMin,
    max: builtInMax,
    range: builtInRange,
    rand: builtInRand,

    // Date
    utcNow: builtInUtcNow,
    addseconds: builtInAddSeconds,
    addminutes: builtInAddMinutes,
    addhours: builtInAddHours,
    adddays: builtInAddDays,
    formatDateTime: builtInFormatDateTime,
    startOfHour: builtInStartOfHour,
    startOfDay: builtInStartOfDay,
    startOfMonth: builtInStartOfMonth,
    dayOfWeek: builtInDayOfWeek,
    dayOfMonth: builtInDayOfMonth,
    dayOfYear: builtInDayOfYear,
    ticks: builtInTicks
};

function getTypeNameForExpression(object: any): TypeNameForExpression {
    if (object === undefined || object === null) {
        return 'Null';
    }
    const typeOfObj = typeof object;
    if (typeOfObj === 'string') {
        return 'String';
    }
    if (typeOfObj === 'number') {
        return 'Number';
    }
    if (typeOfObj === 'boolean') {
        return 'Boolean';
    }
    if (object.constructor === Array) {
        return 'Array';
    }
    const getType = {};
    if (object && getType.toString.call(object) === '[object Function]') {
        return 'Function';
    }
    return 'Object';
}

function checkBuiltInFunctionParameter(functionName: string, expectedLength: number, expectedTypes: TypeNameForExpression[][], args: any): void {
    if (args.length !== expectedLength) {
        throw new Error(format(stringResources.Expression_ParameterNumberMismatch_ErrorMessage, '' + expectedLength, functionName, '' + args.length));
    }
    expectedTypes.forEach((expected, index) => {
        if (expected !== null && index < args.length) {
            const parameterType = getTypeNameForExpression(args[index]);
            if (expected.indexOf(parameterType) < 0) {
                throw new Error(format(stringResources.Expression_ParameterTypeMismatch_ErrorMessage, '' + index, functionName, parameterType));
            }
        }
    });
}

function checkBuiltInFunctionParameterType(functionName: string, index: number, expectedTypes: TypeNameForExpression[], arg: any): void {
    const parameterType = getTypeNameForExpression(arg);
    if (expectedTypes.indexOf(parameterType) < 0) {
        throw new Error(format(stringResources.Expression_ParameterTypeMismatch_ErrorMessage, '' + index, functionName, parameterType));
    }
}
